import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { forwardRef, Component, Input, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { NgControl, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i1 from 'se-fe-form-service';
import { SeFeFormSelectControlDirective, SE_FE_FORM_FIELD_INPUT } from 'se-fe-form-service';

class SeFeFormFieldSelectComponent extends SeFeFormSelectControlDirective {
    constructor(renderer, elementRef, formService, injector) {
        super(renderer, elementRef, injector, formService);
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.formService = formService;
        this.injector = injector;
        this.seFeDataAllowClear = true;
        this.options = [];
    }
    ngOnChanges() {
        this.updateOptions();
    }
    get showEmptyValue() {
        return this.seFeDataAllowClear || !this.value;
    }
    get selectId() {
        return this.labelFor ? this.labelFor : null;
    }
    get selectAriaDesc() {
        return this.helpErrorIds ? this.helpErrorIds : null;
    }
    get form() {
        const control = this.injector.get(NgControl);
        return control.control.parent;
    }
    updateOptions() {
        const seDataOptions = this.seFeDataOptions || [];
        const { options } = this;
        options.length = 0;
        seDataOptions.forEach(option => {
            const value = this.optionValue(option);
            const opt = { label: this.optionLabel(option), disabled: option.disabled ? true : false };
            if (Array.isArray(value))
                opt.values = value;
            else
                opt.value = value;
            options.push(opt);
        });
    }
    optionLabel(option) {
        if (!option)
            return '';
        return option[this.seFeDataLabelKey] || option.label || option.toString();
    }
    optionValue(option) {
        if (!option)
            return '';
        return option[this.seFeDataValueKey] || option.value || option;
    }
    get parentFormControl() {
        const control = this.injector.get(NgControl);
        if (!control)
            return;
        // Test setup uses `this.control.control`. I couldn't figure out how to mock the parent relationship correctly.
        if (control.control)
            return control.control;
        // The formControlName API says .control should return the formControl, but is does not, so we look it up from the _parent.
        return control._parent.control.controls[control.name];
    }
}
SeFeFormFieldSelectComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldSelectComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i1.SeFeFormService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
SeFeFormFieldSelectComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFormFieldSelectComponent, selector: "se-fe-form-field-select", inputs: { seFeDataPlaceholder: "seFeDataPlaceholder", seFeDataValueKey: "seFeDataValueKey", seFeDataLabelKey: "seFeDataLabelKey", seFeDataOptions: "seFeDataOptions", seFeDataComfy: "seFeDataComfy", seFeDataDisabled: "seFeDataDisabled", seFeDataAllowClear: "seFeDataAllowClear" }, providers: [
        {
            provide: SE_FE_FORM_FIELD_INPUT,
            useExisting: SeFeFormFieldSelectComponent,
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SeFeFormFieldSelectComponent),
            multi: true,
        },
    ], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<form [formGroup]=\"form\">\n  <select\n    [attr.id]=\"selectId\"\n    [formControlName]=\"control.name\"\n    (blur)=\"touched($event)\"\n    [ngClass]=\"{\n      'se-fe-form-control-select': !seFeDataComfy,\n      'se-fe-form-control-select--comfy': seFeDataComfy,\n      'se-fe-form-control-select--empty': !control.value,\n      'se-fe-form-control-select--errors': errors.length\n    }\"\n    [attr.aria-describedby]=\"selectAriaDesc\"\n    [attr.aria-disabled]=\"seFeDataDisabled\"\n    [attr.disabled]=\"seFeDataDisabled ? '' : null\">\n    <option *ngIf=\"showEmptyValue\" value=\"\">{{seFeDataPlaceholder || '--'}}</option>\n    <ng-container *ngFor=\"let option of options\">\n      <optgroup *ngIf=\"option.values\" [label]=\"option.label\" [attr.disabled]=\"option.disabled ? '': null\" [attr.aria-disabled]=\"option.disabled\">\n        <option *ngFor=\"let opt of option.values\" [value]=\"opt.value\">{{ opt.label }}</option>\n      </optgroup>\n      <option *ngIf=\"option.value\" [value]=\"option.value\" [attr.aria-disabled]=\"option.disabled\" [attr.disabled]=\"option.disabled ? '': null\">{{ option.label }}</option>\n    </ng-container>\n  </select>\n</form>\n", styles: [".se-fe-form-control-select--comfy,.se-fe-form-control-select{-moz-appearance:none;-webkit-appearance:none;background-color:#fff;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 24 24' width='24' preserveAspectRatio='xMidYMid meet' aria-labelledby='title desc' role='img'%3E%3Ctitle id='title'%3Echevron-down%3C/title%3E%3Cpath fill='%2368767f' stroke-width='0' d='M19.4343146,7.43431458 C19.746734,7.12189514 20.253266,7.12189514 20.5656854,7.43431458 C20.8781049,7.74673401 20.8781049,8.25326599 20.5656854,8.56568542 L12.5656854,16.5656854 C12.253266,16.8781049 11.746734,16.8781049 11.4343146,16.5656854 L3.43431458,8.56568542 C3.12189514,8.25326599 3.12189514,7.74673401 3.43431458,7.43431458 C3.74673401,7.12189514 4.25326599,7.12189514 4.56568542,7.43431458 L12,14.8686292 L19.4343146,7.43431458 Z'%3E%3C/path%3E%3C/svg%3E\");background-repeat:no-repeat;background-size:14px 14px;border-radius:4px;border-style:solid;border-width:1px;box-sizing:border-box;color:#23282b;cursor:pointer;font-size:calc(var(--se-rem, 1rem) * 1);line-height:18px;width:100%;border-color:#c1cace}.se-fe-form-control-select--comfy:hover,.se-fe-form-control-select:hover{border-color:#90a0a7}.se-fe-form-control-select--comfy:focus-within,.se-fe-form-control-select:focus-within{outline:0;box-shadow:0 0 0 calc(0px + 2px) #fff,0 0 0 calc(0px + 4px) #00668f,0 0 0 calc(0px + 6px) #fff}.se-fe-form-control-select--errors.se-fe-form-control-select--comfy,.se-fe-form-control-select--errors.se-fe-form-control-select{border-color:#b42846}.se-fe-form-control-select--errors.se-fe-form-control-select--comfy:hover,.se-fe-form-control-select--errors.se-fe-form-control-select:hover{border-color:#821d33}.se-fe-form-control-select--errors.se-fe-form-control-select--comfy:focus-within,.se-fe-form-control-select--errors.se-fe-form-control-select:focus-within{outline:0;box-shadow:0 0 0 calc(0px + 2px) #fff,0 0 0 calc(0px + 4px) #00668f,0 0 0 calc(0px + 6px) #fff}.se-fe-form-control-select{background-position:right 12px top 50%;padding:8px 38px 8px 12px}.se-fe-form-control-select--comfy{background-position:right 21px top 50%;padding:14px 56px 14px 21px}.se-fe-form-control-select--empty{color:#c1cace}.se-fe-form-control-select--empty option:enabled{color:#23282b}\n"], directives: [{ type: i2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { type: i2.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i2.FormControlName, selector: "[formControlName]", inputs: ["disabled", "formControlName", "ngModel"], outputs: ["ngModelChange"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { type: i2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldSelectComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-form-field-select',
                    templateUrl: './se-fe-form-field-select.component.html',
                    styleUrls: ['./se-fe-form-field-select.component.scss'],
                    providers: [
                        {
                            provide: SE_FE_FORM_FIELD_INPUT,
                            useExisting: SeFeFormFieldSelectComponent,
                        },
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SeFeFormFieldSelectComponent),
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i1.SeFeFormService }, { type: i0.Injector }]; }, propDecorators: { seFeDataPlaceholder: [{
                type: Input
            }], seFeDataValueKey: [{
                type: Input
            }], seFeDataLabelKey: [{
                type: Input
            }], seFeDataOptions: [{
                type: Input
            }], seFeDataComfy: [{
                type: Input
            }], seFeDataDisabled: [{
                type: Input
            }], seFeDataAllowClear: [{
                type: Input
            }] } });

class SeFeFormFieldSelectModule {
}
SeFeFormFieldSelectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeFormFieldSelectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldSelectModule, declarations: [SeFeFormFieldSelectComponent], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule], exports: [FormsModule,
        ReactiveFormsModule,
        SeFeFormFieldSelectComponent] });
SeFeFormFieldSelectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldSelectModule, imports: [[
            CommonModule,
            FormsModule,
            ReactiveFormsModule,
        ], FormsModule,
        ReactiveFormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SeFeFormFieldSelectComponent],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                    ],
                    exports: [
                        FormsModule,
                        ReactiveFormsModule,
                        SeFeFormFieldSelectComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SeFeFormFieldSelectComponent, SeFeFormFieldSelectModule };
